"use client";

import { BiSearch } from "react-icons/bi";
import { format, parseISO } from "date-fns";
import unorm from "unorm";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatISO } from "date-fns";
import qs from "query-string";
import formatPrice from "@/app/utils/formatPrice";

import CalendarSearch from "../inputs/CalendarSearch";
import { toast } from "react-hot-toast";
import { MdMyLocation } from "react-icons/md";
import { IoLocationOutline } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import { FaGlobe, FaUmbrellaBeach, FaWater, FaFish, FaMountain, FaMapMarkerAlt } from "react-icons/fa";
import { GiIsland, GiTurtle } from "react-icons/gi";
import convertPrice from "@/app/utils/CurrencyConverter";
import { ExchangeCurrency } from "@prisma/client";
import { SimplifiedUser } from "@/app/types";
import useCurrencies from "@/app/hooks/useCurrencies";
import useLocations, { Location } from "@/app/hooks/useLocations";
import LocationAutocomplete from "../Autocomplete";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import {
  NominatimAddress,
  resolveNearbyLocationFromNominatim,
} from "@/app/utils/resolveNearbyLocation";

interface QueryParams {
  [key: string]: string | undefined;
}

interface SearchProps {
  currentUser?: SimplifiedUser | null;
  rates?: ExchangeCurrency | null;
  /** After successful search navigation (e.g. close mobile inline search). */
  onSearchComplete?: () => void;
}

/** Liveaboards list uses its own query route; everything else uses `/search`. */
function searchDestinationPath(pathname: string | null): string {
  if (!pathname) return "/search";
  if (pathname.startsWith("/liveaboards")) return "/liveaboards";
  return "/search";
}

const Search: React.FC<SearchProps> = ({
  currentUser,
  rates,
  onSearchComplete,
}) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { selectedCurrency } = useCurrencyStore();
  const userCurrency = currentUser?.currency || selectedCurrency?.code || "USD";
  const { getByCode } = useCurrencies();
  const currentCode = getByCode(userCurrency as string);
  const newCurrencySymbol = currentCode?.symbol || "$";
  // On /liveaboards the suggestions are where the boats sail, not where the
  // dive centers are.
  const locationScope = pathname?.startsWith("/liveaboards")
    ? "liveaboards"
    : "courses";
  const { searchLocations, popular: liveaboardPopular } =
    useLocations(locationScope);

  const paramsDate = searchParams?.get("date");
  const paramsCityOrCountry = searchParams?.get("cityOrCountry");
  const paramsPrice = searchParams?.get("maxPrice");

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const locationMenuRef = useRef<HTMLDivElement>(null);
  const maxPriceRef = useRef<number>(paramsPrice ? parseInt(paramsPrice) : 0);

  const [maxPrice, setMaxPrice] = useState(paramsPrice ? parseInt(paramsPrice) : 0);
  const [displayPrice, setDisplayPrice] = useState(paramsPrice ? parseInt(paramsPrice) : 0);
  
  // Keep ref in sync with state
  useEffect(() => {
    maxPriceRef.current = maxPrice;
  }, [maxPrice]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(paramsDate ? parseISO(paramsDate) : null);
  const [cityOrCountry, setCityOrCountry] = useState<string>(paramsCityOrCountry || "");
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hasSearch, setHasSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isNearbyLoading, setIsNearbyLoading] = useState(false);

  // Convert USD price from URL to user's currency
  useEffect(() => {
    const convertPriceFromUSD = async () => {
      if (paramsPrice && rates && userCurrency !== "USD") {
        try {
          const priceInUSD = parseInt(paramsPrice);
          const convertedPrice = await convertPrice(
            priceInUSD,
            "USD",
            userCurrency,
            rates
          );
          setDisplayPrice(Math.round(convertedPrice));
        } catch (error) {
          console.error("Error converting price from USD:", error);
          setDisplayPrice(parseInt(paramsPrice));
        }
      } else if (paramsPrice) {
        setDisplayPrice(parseInt(paramsPrice));
      } else {
        setDisplayPrice(0);
      }
    };

    convertPriceFromUSD();
  }, [paramsPrice, userCurrency, rates]);
  // When currency changes, convert existing displayPrice from USD to new currency
  // (displayPrice is always stored in USD internally, but displayed in user's currency)
  useEffect(() => {
    const convertDisplayPrice = async () => {
      // If we have maxPrice (USD value from URL or previous selection) and rates
      if (maxPrice > 0 && rates && userCurrency) {
        if (userCurrency === "USD") {
          setDisplayPrice(maxPrice);
        } else {
          try {
            const convertedPrice = await convertPrice(
              maxPrice,
              "USD",
              userCurrency,
              rates
            );
            setDisplayPrice(Math.round(convertedPrice));
          } catch (error) {
            console.error("Error converting display price:", error);
            setDisplayPrice(maxPrice);
          }
        }
      } else if (maxPrice === 0) {
        setDisplayPrice(0);
      }
    };

    convertDisplayPrice();
  }, [userCurrency, rates, maxPrice]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideSearch =
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node);

      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target as Node);

      const isOutsideLocationMenu =
        locationMenuRef.current &&
        !locationMenuRef.current.contains(event.target as Node);

      if (
        isOutsideSearch &&
        (activeSection !== "location" ||
          (activeSection === "location" && isOutsideLocationMenu)) &&
        ((activeSection !== "dates" && activeSection !== "budget") ||
          isOutsideMenu)
      ) {
        setActiveSection(null);
      }
    };

    if (activeSection) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [activeSection]);

  useEffect(() => {
    if (paramsDate) {
      setSelectedDate(parseISO(paramsDate));
    } else {
      setSelectedDate(null);
    }

    if (paramsCityOrCountry) {
      setCityOrCountry(paramsCityOrCountry);
    } else {
      setCityOrCountry("");
    }

    if (paramsPrice) {
      const priceValue = parseInt(paramsPrice);
      setMaxPrice(priceValue);
      maxPriceRef.current = priceValue;
    } else {
      setMaxPrice(0);
      maxPriceRef.current = 0;
      setDisplayPrice(0);
    }
  }, [paramsDate, paramsCityOrCountry, paramsPrice]);

  const clearFilter = (filterName: string) => {
    let currentQuery: QueryParams = {};

    if (searchParams) {
      currentQuery = (pathname?.startsWith("/gifts") ? {} : qs.parse(searchParams.toString())) as QueryParams;
      delete currentQuery[filterName];
    }

    const url = qs.stringifyUrl(
      {
        url: searchDestinationPath(pathname ?? null),
        query: currentQuery,
      },
      { skipNull: true }
    );

    router.push(url);
    router.refresh();
  };

  const resetAllFilters = () => {
    const dest = pathname?.startsWith("/liveaboards") ? "/liveaboards" : "/";
    router.push(dest);
    router.refresh();
    setSelectedDate(null);
    setMaxPrice(0);
    maxPriceRef.current = 0;
    setDisplayPrice(0);
    setCityOrCountry("");
  };

  const handleSectionClick = (sectionName: string) => {
    if (activeSection === sectionName) {
      setActiveSection(null);
    } else {
      setActiveSection(sectionName);
    }
  };

  const capitalizeEachWord = (str: string): string => {
    if (!str) return str;
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const normalizeString = (input: string): string => {
    return unorm
      .nfd(input)
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  };

  const locationLabel = useMemo(() => {
    if (cityOrCountry) {
      return capitalizeEachWord(cityOrCountry);
    }
    return "Anywhere";
  }, [cityOrCountry]);

  const durationLabel = useMemo(() => {
    if (selectedDate) {
      return format(selectedDate, "d/MM/yy");
    }
    return "Date";
  }, [selectedDate]);

  const maxPriceLabel = useMemo(() => {
    if (displayPrice) {
      const formattedPrice = formatPrice(displayPrice);
      return (
        <>
          {newCurrencySymbol}
          {formattedPrice}
        </>
      );
    }
    return "Any";
  }, [displayPrice, newCurrencySymbol]);

  // Budget ranges converted to user's currency
  const [budgetRanges, setBudgetRanges] = useState<
    Array<{ id: string; label: string; max: number }>
  >([
    { id: "any", label: "Any", max: 0 },
    { id: "0-200", label: `0 - ${newCurrencySymbol}200`, max: 200 },
    { id: "200-500", label: `${newCurrencySymbol}200 - ${newCurrencySymbol}500`, max: 500 },
    { id: "500-1000", label: `${newCurrencySymbol}500 - ${newCurrencySymbol}1000`, max: 1000 },
    { id: "1000+", label: `${newCurrencySymbol}1000+`, max: 1500 },
  ]);

  // Convert budget ranges from USD to user's currency when currency or rates change
  useEffect(() => {
    const convertRanges = async () => {
      const usdRanges = [
        { id: "any", max: 0 },
        { id: "0-200", max: 200 },
        { id: "200-500", max: 500 },
        { id: "500-1000", max: 1000 },
        { id: "1000+", max: 1500 },
      ];

      if (userCurrency === "USD" || !rates) {
        setBudgetRanges([
          { id: "any", label: "Any", max: 0 },
          { id: "0-200", label: `0 - ${newCurrencySymbol}200`, max: 200 },
          { id: "200-500", label: `${newCurrencySymbol}200 - ${newCurrencySymbol}500`, max: 500 },
          { id: "500-1000", label: `${newCurrencySymbol}500 - ${newCurrencySymbol}1000`, max: 1000 },
          { id: "1000+", label: `${newCurrencySymbol}1000+`, max: 1500 },
        ]);
        return;
      }

      try {
        // Helper function to round to nice numbers
        const roundToNiceNumber = (num: number): number => {
          if (num < 100) {
            return Math.round(num / 10) * 10; // Round to nearest 10
          } else if (num < 1000) {
            return Math.round(num / 50) * 50; // Round to nearest 50
          } else if (num < 10000) {
            return Math.round(num / 100) * 100; // Round to nearest 100
          } else if (num < 100000) {
            return Math.round(num / 1000) * 1000; // Round to nearest 1000
          } else if (num < 1000000) {
            return Math.round(num / 10000) * 10000; // Round to nearest 10000
          } else {
            return Math.round(num / 100000) * 100000; // Round to nearest 100000
          }
        };

        const formatNumber = (num: number): string => formatPrice(num);

        const convertedRanges = await Promise.all(
          usdRanges.map(async (range) => {
            if (range.max === 0) {
              return { id: "any", label: "Any", max: 0 };
            }

            const converted = await convertPrice(
              range.max,
              "USD",
              userCurrency,
              rates
            );
            const rounded = roundToNiceNumber(converted);

            if (range.id === "0-200") {
              return {
                id: "0-200",
                max: rounded,
                label: `0 - ${newCurrencySymbol}${formatNumber(rounded)}`,
              };
            } else if (range.id === "200-500") {
              const minConverted = await convertPrice(200, "USD", userCurrency, rates);
              const minRounded = roundToNiceNumber(minConverted);
              return {
                id: "200-500",
                max: rounded,
                label: `${newCurrencySymbol}${formatNumber(minRounded)} - ${newCurrencySymbol}${formatNumber(rounded)}`,
              };
            } else if (range.id === "500-1000") {
              const minConverted = await convertPrice(500, "USD", userCurrency, rates);
              const minRounded = roundToNiceNumber(minConverted);
              return {
                id: "500-1000",
                max: rounded,
                label: `${newCurrencySymbol}${formatNumber(minRounded)} - ${newCurrencySymbol}${formatNumber(rounded)}`,
              };
            } else {
              const minConverted = await convertPrice(1000, "USD", userCurrency, rates);
              const minRounded = roundToNiceNumber(minConverted);
              return {
                id: "1000+",
                max: rounded,
                label: `${newCurrencySymbol}${formatNumber(minRounded)}+`,
              };
            }
          })
        );

        setBudgetRanges(convertedRanges);
      } catch (error) {
        console.error("Error converting budget ranges:", error);
        // Fallback to USD ranges
        setBudgetRanges([
          { id: "any", label: "Any", max: 0 },
          { id: "0-200", label: `0 - ${newCurrencySymbol}200`, max: 200 },
          { id: "200-500", label: `${newCurrencySymbol}200 - ${newCurrencySymbol}500`, max: 500 },
          { id: "500-1000", label: `${newCurrencySymbol}500 - ${newCurrencySymbol}1000`, max: 1000 },
          { id: "1000+", label: `${newCurrencySymbol}1000+`, max: 1500 },
        ]);
      }
    };

    convertRanges();
  }, [userCurrency, rates, newCurrencySymbol]);

  const onSubmit = useCallback(async () => {
    if (isSearching) return; // Prevent multiple clicks
    
    setIsSearching(true);
    
    try {
      let currentQuery: QueryParams = {};

      if (searchParams) {
        currentQuery = (pathname?.startsWith("/gifts") ? {} : qs.parse(searchParams.toString())) as QueryParams;
      }

      const updatedQuery: QueryParams = {
        ...currentQuery,
      };

      if (cityOrCountry) {
        updatedQuery.cityOrCountry = cityOrCountry.trim();
      } else {
        delete updatedQuery.cityOrCountry;
      }

      if (selectedDate) {
        updatedQuery.date = formatISO(selectedDate);
      } else {
        delete updatedQuery.date;
      }

      // Use maxPriceRef to get the most current value (always in USD)
      const currentMaxPrice = maxPriceRef.current;
      if (currentMaxPrice > 0) {
        updatedQuery.maxPrice = currentMaxPrice.toString();
      } else {
        delete updatedQuery.maxPrice;
      }

      const url = qs.stringifyUrl(
        {
          url: searchDestinationPath(pathname ?? null),
          query: updatedQuery,
        },
        { skipNull: true }
      );
      
      setActiveSection(null);
      setHasSearch(true);
      router.push(url);
      router.refresh();
    } catch (error) {
      console.error('Error during search:', error);
    } finally {
      // Keep loading state briefly to show feedback
      setTimeout(() => {
        setIsSearching(false);
        onSearchComplete?.();
      }, 500);
    }
  }, [
    router,
    searchParams,
    selectedDate,
    cityOrCountry,
    userCurrency,
    rates,
    pathname,
    maxPrice,
    isSearching,
    onSearchComplete,
  ]);

  const handleLocationClear = useCallback(() => {
    setCityOrCountry("");
    clearFilter("cityOrCountry");
  }, []);

  const handleDateClear = useCallback(() => {
    setSelectedDate(null);
    clearFilter("date");
  }, []);

  const handleBudgetClear = useCallback(() => {
    setDisplayPrice(0);
    setMaxPrice(0);
    maxPriceRef.current = 0;
    clearFilter("maxPrice");
  }, []);

  const applyLocationAndSearch = useCallback(
    (nextLocation: string | null) => {
      let currentQuery: QueryParams = {};

      if (searchParams) {
        currentQuery = (pathname?.startsWith("/gifts") ? {} : qs.parse(searchParams.toString())) as QueryParams;
      }

      const updatedQuery: QueryParams = {
        ...currentQuery,
      };

      if (nextLocation && nextLocation.trim().length > 0) {
        updatedQuery.cityOrCountry = nextLocation.trim();
      } else {
        delete updatedQuery.cityOrCountry;
      }

      const url = qs.stringifyUrl(
        {
          url: searchDestinationPath(pathname ?? null),
          query: updatedQuery,
        },
        { skipNull: true },
      );

      router.push(url);
      router.refresh();
      setActiveSection(null);
      setHasSearch(true);
    },
    [router, searchParams, pathname],
  );

  const handleLocationSelect = useCallback(
    (location: Location) => {
      setCityOrCountry(location.displayName);
      applyLocationAndSearch(location.displayName);
    },
    [applyLocationAndSearch],
  );

  const searchNearby = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported in this browser");
      return;
    }

    if (isNearbyLoading) return;

    setIsNearbyLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `/api/geocode/reverse?lat=${latitude}&lon=${longitude}`,
          );
          const data = await response.json();

          if (!response.ok || !data.address) {
            toast.error("Location not found");
            return;
          }

          const location = resolveNearbyLocationFromNominatim(
            data.address as NominatimAddress,
            searchLocations,
          );

          if (!location) {
            toast.error("Location not found");
            return;
          }

          setCityOrCountry(location);
          applyLocationAndSearch(location);
        } catch (error) {
          console.error("Error fetching geocoding data:", error);
          toast.error("Error getting location");
        } finally {
          setIsNearbyLoading(false);
        }
      },
      (error) => {
        console.error("Error obtaining location:", error.message, error.code);
        setIsNearbyLoading(false);
        if ((error as GeolocationPositionError).code === 1) {
          toast.error("Location access blocked. Check site permissions in your browser.");
        } else if ((error as GeolocationPositionError).code === 3) {
          toast.error("Location request timed out. Try again.");
        } else {
          toast.error("Could not access your location");
        }
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 },
    );
  }, [isNearbyLoading, searchLocations, applyLocationAndSearch]);

  return (
    <div className="relative">
      <div className="flex items-center mt-2 md:mt-1">
        <div className="w-full px-1 sm:px-0">
          <div
            ref={searchContainerRef}
            className="
              w-full 
              mx-auto 
              rounded-full 
              border border-gray-200 
              bg-white/90 
              shadow-sm 
              hover:shadow-md 
              transition 
              cursor-pointer
              h-[54px]
            "
          >
            <div className="flex flex-row items-center justify-between h-full">
              {/* WHERE */}
              <div
                className="relative flex-1 sm:max-w-xs px-4 sm:px-6 text-left truncate text-gray-700"
                onClick={() => handleSectionClick("location")}
                title={locationLabel}
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                  Where
                </div>
                <div className="text-sm sm:text-base font-semibold pr-4">
                  {locationLabel}
                </div>
                {cityOrCountry && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLocationClear();
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                  >
                    <IoMdClose size={14} />
                  </button>
                )}
              </div>

              {/* WHEN */}
              <div
                className="relative flex-1 sm:flex-[0.9] px-4 sm:px-6 border-x border-gray-200 text-left text-gray-700"
                onClick={() => handleSectionClick("dates")}
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                  When
                </div>
                <div className="text-sm sm:text-base font-semibold pr-4">
                  {durationLabel}
                </div>
                {selectedDate && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateClear();
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                  >
                    <IoMdClose size={14} />
                  </button>
                )}
              </div>

              {/* BUDGET */}
              <div
                className="relative flex-1 sm:flex-[0.8] px-4 sm:px-6 text-left text-gray-700"
                onClick={() => handleSectionClick("budget")}
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                  Budget
                </div>
                <div className="text-sm sm:text-base font-semibold truncate pr-4">
                  {maxPriceLabel}
                </div>
                {displayPrice > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBudgetClear();
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                  >
                    <IoMdClose size={14} />
                  </button>
                )}
              </div>
              <button
                onClick={onSubmit}
                disabled={isSearching}
                className={`
                  mr-1
                  sm:mr-2
                  flex 
                  items-center 
                  justify-center 
                  ${isSearching 
                    ? 'bg-cyan-600 cursor-wait' 
                    : 'bg-cyan-700 hover:bg-cyan-600'
                  }
                  text-white 
                  rounded-full 
                  p-2.5 
                  sm:p-3 
                  shadow-sm
                  transition-all
                  duration-200
                  disabled:opacity-75
                `}
              >
                {isSearching ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="hidden sm:inline text-sm font-semibold ml-2">
                      Searching...
                    </span>
                  </>
                ) : (
                  <>
                    <BiSearch size={18} />
                    <span className="hidden sm:inline text-sm font-semibold ml-2">
                      Search
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeSection === "location" && (
        <div
          ref={locationMenuRef}
          className="
            absolute 
            z-50
            top-full 
            mt-3 
            left-1/2 
            -translate-x-1/2 
            max-w-[640px] 
            w-[92vw] 
            sm:w-4/5 
            rounded-3xl 
            bg-white 
            border 
            border-gray-100 
            shadow-2xl 
            transition 
            font-medium 
            text-gray-900
          "
        >
          <div className="px-5 pt-4 pb-2 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Where do you want to dive?
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Search destinations or choose one of the popular dive spots.
            </p>
          </div>

          <div className="px-4 pt-3 pb-4 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Search input */}
            <div className="rounded-full border border-gray-200 bg-gray-50/60 px-3 py-1.5">
              <LocationAutocomplete
                value={cityOrCountry}
                onSelect={handleLocationSelect}
                onClear={handleLocationClear}
                onChange={(e) => setCityOrCountry(e.target.value)}
                placeholder="Search destinations..."
                className="w-full border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
                maxSuggestions={8}
                scope={locationScope}
              />
            </div>

            {/* Nearby */}
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Nearby
              </div>
              <button
                type="button"
                disabled={isNearbyLoading}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-2xl border border-gray-200 bg-gray-50 hover:bg-gray-100 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  searchNearby();
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                    {isNearbyLoading ? (
                      <span className="h-4 w-4 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MdMyLocation size={18} />
                    )}
                  </span>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Nearby</div>
                    <div className="text-xs text-gray-500">
                      {isNearbyLoading
                        ? "Finding dive spots near you..."
                        : "Dive spots close to you"}
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Popular destinations */}
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Popular destinations
              </div>
              <div className="space-y-2">
                {/* On /liveaboards the picks are where the most boats sail,
                    ranked by the backend; the course picks stay as they are. */}
                {(locationScope === "liveaboards"
                  ? [
                      {
                        id: "anywhere",
                        title: "Anywhere",
                        subtitle: "Explore all destinations",
                        icon: FaGlobe,
                        iconColor: "text-blue-500",
                        value: "",
                      },
                      ...liveaboardPopular.map((dest) => ({
                        id: dest.value,
                        title: dest.name,
                        subtitle: dest.subtitle,
                        icon: FaWater,
                        iconColor: "text-blue-600",
                        value: dest.value,
                      })),
                    ]
                  : [
                  {
                    id: "anywhere",
                    title: "Anywhere",
                    subtitle: "Explore all destinations",
                    icon: FaGlobe,
                    iconColor: "text-blue-500",
                    value: "",
                  },
                  {
                    id: "tenerife",
                    title: "Tenerife",
                    subtitle: "Canary Islands, Spain",
                    icon: GiIsland,
                    iconColor: "text-blue-500",
                    value: "Tenerife, Spain",
                  },
                  {
                    id: "bali",
                    title: "Bali",
                    subtitle: "Indonesia",
                    icon: GiIsland,
                    iconColor: "text-cyan-600",
                    value: "Bali, Indonesia",
                  },
                  {
                    id: "gili-trawangan",
                    title: "Gili Trawangan",
                    subtitle: "Lombok, Indonesia",
                    icon: GiIsland,
                    iconColor: "text-emerald-600",
                    value: "Gili Trawangan, Indonesia",
                  },
                  // {
                  //   id: "koh-tao",
                  //   title: "Koh Tao",
                  //   subtitle: "Thailand",
                  //   icon: FaFish,
                  //   iconColor: "text-teal-500",
                  //   value: "Koh Tao, Thailand",
                  // },
                  {
                    id: "red-sea",
                    title: "Red Sea",
                    subtitle: "Egypt",
                    icon: FaWater,
                    iconColor: "text-blue-600",
                    value: "Red Sea, Egypt",
                  },
                  {
                    id: "riviera-maya",
                    title: "Riviera Maya",
                    subtitle: "Mexico",
                    icon: FaUmbrellaBeach,
                    iconColor: "text-amber-500",
                    value: "Riviera Maya, Mexico",
                  },
                  // {
                  //   id: "palawan",
                  //   title: "Palawan",
                  //   subtitle: "Philippines",
                  //   icon: GiIsland,
                  //   iconColor: "text-emerald-500",
                  //   value: "Palawan, Philippines",
                  // },
                  {
                    id: "greece",
                    title: "Greece",
                    subtitle: "Mediterranean",
                    icon: FaUmbrellaBeach,
                    iconColor: "text-blue-500",
                    value: "Greece",
                  },
                  {
                    id: "maldives",
                    title: "Maldives",
                    subtitle: "Indian Ocean",
                    icon: GiIsland,
                    iconColor: "text-cyan-500",
                    value: "Maldives",
                  },
                  {
                    id: "cyprus",
                    title: "Cyprus",
                    subtitle: "Mediterranean",
                    icon: GiIsland,
                    iconColor: "text-blue-500",
                    value: "Cyprus",
                  },
                  // {
                  //   id: "komodo",
                  //   title: "Komodo",
                  //   subtitle: "Indonesia",
                  //   icon: GiTurtle,
                  //   iconColor: "text-orange-500",
                  //   value: "Komodo, Indonesia",
                  // },
                ]).map((dest) => {
                  const IconComponent = dest.icon;
                  return (
                    <button
                      key={dest.id}
                      type="button"
                      onClick={() => {
                        setCityOrCountry(dest.value);
                        if (dest.value === "") {
                          applyLocationAndSearch(null);
                        } else {
                          applyLocationAndSearch(dest.value);
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-sm transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gray-50 ${dest.iconColor}`}>
                          <IconComponent size={18} />
                        </span>
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">
                            {dest.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {dest.subtitle}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection === "dates" && (
        <div
          ref={menuRef}
          className="
            absolute
            top-full
            mt-3
            left-1/2
            -translate-x-1/2
            z-50
            max-w-[720px]
            w-[92vw]
            sm:w-4/5
            rounded-3xl
            bg-white
            border
            border-gray-100
            shadow-2xl
            transition
            cursor-pointer
            font-medium
            text-gray-900
          "
        >
          <div className="px-4 pt-4 pb-2 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              When are you going?
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Choose your dive dates.
            </p>
          </div>
          <div className="px-2 sm:px-4 py-3 sm:py-4">
            <CalendarSearch
              selectedDate={selectedDate !== null ? selectedDate : undefined}
              onChange={(value) => setSelectedDate(value)}
            />
          </div>
        </div>
      )}

      {activeSection === "budget" && (
        <div
          ref={menuRef}
          className="
            absolute
            top-full
            mt-3
            left-1/2
            -translate-x-1/2
            z-50
            max-w-[480px]
            w-[92vw]
            sm:w-3/5
            rounded-3xl
            bg-white
            border
            border-gray-100
            shadow-2xl
            transition
            font-medium
            text-gray-900
          "
        >
          <div className="px-4 pt-4 pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  What&apos;s your budget?
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Price per diver in {newCurrencySymbol}.
                </p>
              </div>
              {displayPrice > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setDisplayPrice(0);
                    setMaxPrice(0);
                    maxPriceRef.current = 0;
                    clearFilter("maxPrice");
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear budget"
                >
                  <IoMdClose size={20} />
                </button>
              )}
            </div>
          </div>

          <div className="px-4 py-3 space-y-2">

            {budgetRanges.map((option) => {
              const isSelected =
                (option.id === "any" && displayPrice === 0) ||
                (option.id !== "any" && displayPrice === option.max);

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={async () => {
                    if (option.id === "any") {
                      setDisplayPrice(0);
                      const zeroPrice = 0;
                      setMaxPrice(zeroPrice);
                      maxPriceRef.current = zeroPrice;
                    } else {
                      // option.max is already in user's currency
                      setDisplayPrice(option.max);
                      // Convert back to USD for maxPrice (which is used in URL)
                      if (userCurrency !== "USD" && rates) {
                        try {
                          const usdValue = await convertPrice(
                            option.max,
                            userCurrency,
                            "USD",
                            rates
                          );
                          const roundedUsd = Math.round(usdValue);
                          setMaxPrice(roundedUsd);
                          maxPriceRef.current = roundedUsd;
                        } catch (error) {
                          // Fallback: estimate USD value (not ideal but better than nothing)
                          setMaxPrice(option.max);
                          maxPriceRef.current = option.max;
                        }
                      } else {
                        setMaxPrice(option.max);
                        maxPriceRef.current = option.max;
                      }
                    }
                  }}
                  className={`
                    w-full flex items-center justify-between px-3 py-2.5 rounded-2xl border
                    text-sm
                    ${isSelected
                      ? "border-cyan-600 bg-cyan-50 text-cyan-900"
                      : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`
                        inline-flex h-4 w-4 rounded-full border-2
                        ${isSelected
                          ? "border-cyan-600 bg-cyan-600/10"
                          : "border-gray-300 bg-white"
                        }
                      `}
                    />
                    <span className="font-semibold">{option.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom amount input */}
          <div className="px-4 pb-2">
            <label
              htmlFor="custom-budget"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Or enter a custom max price
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">
                {newCurrencySymbol}
              </span>
              <input
                id="custom-budget"
                type="number"
                min={0}
                value={displayPrice || ""}
                onChange={async (e) => {
                  const newValue = parseInt(e.target.value || "0", 10) || 0;
                  setDisplayPrice(newValue);
                  // Also update maxPrice (USD) for URL
                  if (newValue > 0 && userCurrency !== "USD" && rates) {
                    try {
                      const usdValue = await convertPrice(
                        newValue,
                        userCurrency,
                        "USD",
                        rates
                      );
                      const roundedUsd = Math.round(usdValue);
                      setMaxPrice(roundedUsd);
                      maxPriceRef.current = roundedUsd;
                    } catch (error) {
                      setMaxPrice(newValue);
                      maxPriceRef.current = newValue;
                    }
                  } else {
                    setMaxPrice(newValue);
                    maxPriceRef.current = newValue;
                  }
                }}
                className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-cyan-700 focus:border-cyan-700"
                placeholder="Custom amount"
              />
            </div>
          </div>
        </div>
      )}

      {/* Per-field clear handled inside the pill; no global "Clear all filters" button */}
    </div>
  );
};

export default Search;